/**
 * SendConsentRequestModal — TREATX-L1E provider-side lifecycle initiator
 * ======================================================================
 *
 * Drives the canonical L1-E consent-request lifecycle from the provider:
 *
 *   Step 1 · Patient + treatment  → POST /before-after/consent-requests/draft
 *   Step 2 · Bind exact media     → POST /before-after/consent-requests/{token}/media
 *   Step 3 · Dates + attestations → PATCH /before-after/consent-requests/{token}
 *   Send                          → POST /before-after/consent-requests/{token}/send
 *
 * The `/send` gate is what actually notifies the patient. Every step
 * is validated backend-side; this modal only performs UX validation.
 */
import React, { useState, useRef } from 'react';
import { dateInputLang } from '../../../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Send, Upload, X, ArrowLeft } from 'lucide-react';
import api from '../../../utils/api';
import { getActiveWorkspaceClinicId } from '../../../utils/activeWorkspace';

const initial = {
  patient_email: '',
  patient_user_id: '',
  treatment_slug: '',
  before_media_id: '',
  after_media_id: '',
  procedure_date: '',
  before_captured_at: '',
  after_captured_at: '',
  same_environment_confirmed: false,
  no_retouch_confirmed: false,
  no_privacy_violation_confirmed: false,
  no_incentive_offered_confirmed: false,
};

const SendConsentRequestModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState('');
  const [form, setForm] = useState(initial);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  if (!open) return null;

  const reset = () => { setStep(1); setToken(''); setForm(initial); };
  const dismiss = () => { reset(); onClose?.(); };

  const uploadMedia = async (file) => {
    const clinicId = getActiveWorkspaceClinicId();
    if (!clinicId) throw new Error('NO_CLINIC_SCOPE');
    if (!form.treatment_slug) throw new Error('TREATMENT_SLUG_REQUIRED');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('context_type', 'treatment');
    // TREATX-L1E: treatment context requires "{clinic_id}:{treatment_slug}".
    fd.append('context_id', `${clinicId}:${form.treatment_slug}`);
    const r = await api.post('/clinic/media', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data?.media_id;
  };

  const step1Draft = async () => {
    if (!form.patient_email || !form.treatment_slug) {
      toast.error(t('provider.beforeAfter.l1eRequest.errors.step1Missing'));
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/before-after/consent-requests/draft', {
        patient_email: form.patient_email,
        treatment_slug: form.treatment_slug,
      });
      if (!r.data?.token) throw new Error(r.data?.error || 'DRAFT_FAILED');
      setToken(r.data.token);
      setStep(2);
      toast.success(t('provider.beforeAfter.l1eRequest.toasts.draftCreated'));
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (e, position) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const media_id = await uploadMedia(f);
      setForm({ ...form, [`${position}_media_id`]: media_id });
      toast.success(t('provider.beforeAfter.l1eRequest.toasts.mediaUploaded'));
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const step2BindMedia = async () => {
    if (!form.before_media_id || !form.after_media_id) {
      toast.error(t('provider.beforeAfter.l1eRequest.errors.step2Missing'));
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/before-after/consent-requests/${token}/media`, {
        before_media_id: form.before_media_id,
        after_media_id: form.after_media_id,
      });
      if (!r.data?.success) throw new Error(r.data?.error || 'BIND_FAILED');
      setStep(3);
      toast.success(t('provider.beforeAfter.l1eRequest.toasts.mediaBound'));
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const step3PatchAndSend = async () => {
    if (!form.procedure_date || !form.before_captured_at || !form.after_captured_at
        || !form.same_environment_confirmed || !form.no_retouch_confirmed
        || !form.no_privacy_violation_confirmed || !form.no_incentive_offered_confirmed) {
      toast.error(t('provider.beforeAfter.l1eRequest.errors.step3Missing'));
      return;
    }
    setBusy(true);
    try {
      const p = await api.patch(`/before-after/consent-requests/${token}`, {
        procedure_date: form.procedure_date,
        before_captured_at: form.before_captured_at,
        after_captured_at: form.after_captured_at,
        same_environment_confirmed: form.same_environment_confirmed,
        no_retouch_confirmed: form.no_retouch_confirmed,
        no_privacy_violation_confirmed: form.no_privacy_violation_confirmed,
        no_incentive_offered_confirmed: form.no_incentive_offered_confirmed,
      });
      if (!p.data?.success) throw new Error(p.data?.error || 'PATCH_FAILED');
      const s = await api.post(`/before-after/consent-requests/${token}/send`, {});
      if (!s.data?.success) throw new Error(s.data?.error || 'SEND_FAILED');
      toast.success(t('provider.beforeAfter.l1eRequest.toasts.sent'));
      dismiss();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-auto p-4" data-testid="send-consent-request-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl mt-16 p-6 space-y-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {t('provider.beforeAfter.l1eRequest.titleTemplate', { step })}
          </h2>
          <Button variant="ghost" size="sm" onClick={dismiss} disabled={busy} data-testid="close-consent-request-modal">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {step === 1 && (
          <>
            <div>
              <Label>{t('provider.beforeAfter.l1eRequest.patientEmail')}</Label>
              <Input
                type="email"
                value={form.patient_email}
                onChange={(e) => setForm({ ...form, patient_email: e.target.value })}
                placeholder="patient@example.com"
                data-testid="l1e-req-patient-email"
              />
            </div>
            <div>
              <Label>{t('provider.beforeAfter.l1eRequest.treatmentSlug')}</Label>
              <Input
                value={form.treatment_slug}
                onChange={(e) => setForm({ ...form, treatment_slug: e.target.value })}
                placeholder="e.g. hair-transplant"
                data-testid="l1e-req-treatment-slug"
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={step1Draft} disabled={busy} data-testid="l1e-req-step1-next">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {t('provider.beforeAfter.actions.next')}
              </Button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-sm text-slate-600">{t('provider.beforeAfter.l1eRequest.step2Help')}</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border p-3">
                <Label>{t('provider.beforeAfter.l1eRequest.beforeMedia')}</Label>
                <input ref={beforeRef} type="file" accept="image/*" hidden onChange={(e) => onFile(e, 'before')} />
                <Button variant="outline" size="sm" onClick={() => beforeRef.current?.click()} disabled={busy} data-testid="l1e-req-upload-before" className="mt-1">
                  <Upload className="h-4 w-4 mr-2" />
                  {form.before_media_id ? t('provider.beforeAfter.l1eRequest.replace') : t('provider.beforeAfter.l1eRequest.upload')}
                </Button>
                {form.before_media_id && (
                  <p className="text-xs text-emerald-700 mt-1 font-mono truncate" data-testid="l1e-req-before-id">
                    {form.before_media_id}
                  </p>
                )}
              </div>
              <div className="rounded-md border p-3">
                <Label>{t('provider.beforeAfter.l1eRequest.afterMedia')}</Label>
                <input ref={afterRef} type="file" accept="image/*" hidden onChange={(e) => onFile(e, 'after')} />
                <Button variant="outline" size="sm" onClick={() => afterRef.current?.click()} disabled={busy} data-testid="l1e-req-upload-after" className="mt-1">
                  <Upload className="h-4 w-4 mr-2" />
                  {form.after_media_id ? t('provider.beforeAfter.l1eRequest.replace') : t('provider.beforeAfter.l1eRequest.upload')}
                </Button>
                {form.after_media_id && (
                  <p className="text-xs text-emerald-700 mt-1 font-mono truncate" data-testid="l1e-req-after-id">
                    {form.after_media_id}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} disabled={busy}>
                <ArrowLeft className="h-4 w-4 mr-2" />{t('provider.beforeAfter.actions.back')}
              </Button>
              <Button
                onClick={step2BindMedia}
                disabled={busy || !form.before_media_id || !form.after_media_id}
                data-testid="l1e-req-step2-next"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {t('provider.beforeAfter.actions.next')}
              </Button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>{t('provider.beforeAfter.l1e.fields.procedureDate')}</Label>
                <Input type="date" lang={dateInputLang()} value={form.procedure_date}
                       onChange={(e) => setForm({ ...form, procedure_date: e.target.value })}
                       data-testid="l1e-req-procedure-date" />
              </div>
              <div>
                <Label>{t('provider.beforeAfter.l1e.fields.beforeCapturedAt')}</Label>
                <Input type="date" lang={dateInputLang()} value={form.before_captured_at}
                       onChange={(e) => setForm({ ...form, before_captured_at: e.target.value })}
                       data-testid="l1e-req-before-captured" />
              </div>
              <div>
                <Label>{t('provider.beforeAfter.l1e.fields.afterCapturedAt')}</Label>
                <Input type="date" lang={dateInputLang()} value={form.after_captured_at}
                       onChange={(e) => setForm({ ...form, after_captured_at: e.target.value })}
                       data-testid="l1e-req-after-captured" />
              </div>
            </div>
            <div className="space-y-2 rounded-md border p-3">
              {[
                ['same_environment_confirmed', 'sameEnvironment'],
                ['no_retouch_confirmed', 'noRetouch'],
                ['no_privacy_violation_confirmed', 'noPrivacyViolation'],
                ['no_incentive_offered_confirmed', 'noIncentive'],
              ].map(([f, k]) => (
                <label key={f} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`l1e-req-attest-label-${f}`}>
                  <input
                    type="checkbox"
                    checked={!!form[f]}
                    onChange={(e) => setForm({ ...form, [f]: e.target.checked })}
                    data-testid={`l1e-req-attest-${f}`}
                    className="mt-0.5"
                  />
                  <span>{t(`provider.beforeAfter.l1e.attestations.${k}`)}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} disabled={busy}>
                <ArrowLeft className="h-4 w-4 mr-2" />{t('provider.beforeAfter.actions.back')}
              </Button>
              <Button
                onClick={step3PatchAndSend}
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="l1e-req-send"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                {t('provider.beforeAfter.l1eRequest.send')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SendConsentRequestModal;
